/**
 * Company Timeline — Scroll Reveal + Click Replay
 *
 * Animation architecture:
 *
 * 1. Item Reveal (IntersectionObserver)
 *    Each .timeline__item starts with CSS opacity: 0 and translateY(40px).
 *    When an item enters the viewport (15% visible, 40px bottom inset),
 *    .timeline__item--revealed is added → CSS transition handles the rest.
 *    The observer unobserves immediately — each item reveals only once.
 *    Items reveal one by one as the user scrolls down.
 *
 * 2. Click Replay
 *    Clicking a revealed item:
 *      a) Removes .timeline__item--revealed  (back to hidden)
 *      b) Forces layout reflow (void item.offsetWidth)
 *      c) Re-adds .timeline__item--revealed  (transition replays)
 *      d) Adds .timeline__dot--active on the dot (scale + glow)
 *      e) Adds .timeline__item--emphasis on the item (year highlight)
 *      f) After 1.2s, cleans up the active/emphasis classes
 *
 * 3. Reduced Motion
 *    If prefers-reduced-motion: reduce is active, all items are shown
 *    immediately and no observers are started.
 *
 * Performance:
 *   - No layout-triggering animations (only opacity + transform)
 *   - Observers are disconnected when the element is removed from DOM
 */

if (!customElements.get('company-timeline')) {
  customElements.define(
    'company-timeline',
    class CompanyTimeline extends HTMLElement {
      /** @type {HTMLElement[]} */
      items = [];

      /** @type {IntersectionObserver|null} */
      itemObserver = null;

      /* ── Lifecycle ─────────────────────────────────────────────────── */

      connectedCallback() {
        this.items = [...this.querySelectorAll('.timeline__item')];

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.showAll();
          return;
        }

        this.setupItemObserver();
        this.setupClickHandlers();
      }

      disconnectedCallback() {
        this.itemObserver?.disconnect();

        for (const item of this.items) {
          item.removeEventListener('click', item._clickHandler);
        }
      }

      /* ── Item reveal ───────────────────────────────────────────────── */

      setupItemObserver() {
        this.itemObserver = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              entry.target.classList.add('timeline__item--revealed');
              this.itemObserver.unobserve(entry.target);
            }
          },
          {
            threshold: 0.15,
            rootMargin: '0px 0px -40px 0px'
          }
        );

        for (const item of this.items) {
          this.itemObserver.observe(item);
        }
      }

      /* ── Click to replay ───────────────────────────────────────────── */

      setupClickHandlers() {
        for (const item of this.items) {
          item._clickHandler = () => this.replayItem(item);
          item.addEventListener('click', item._clickHandler);
        }
      }

      /**
       * Replays the reveal animation for a single timeline item.
       * @param {HTMLElement} item — the .timeline__item to replay
       */
      replayItem(item) {
        if (!item.classList.contains('timeline__item--revealed')) return;

        const dot = item.querySelector('.timeline__dot');

        /* 1. Reset to hidden state */
        item.classList.remove('timeline__item--revealed');
        item.classList.remove('timeline__item--emphasis');

        /* 2. Force reflow so the browser registers the removal */
        void item.offsetWidth;

        /* 3. Replay transition + add emphasis */
        item.classList.add('timeline__item--revealed');
        item.classList.add('timeline__item--emphasis');
        dot?.classList.add('timeline__dot--active');

        /* 4. Clean up after transition */
        setTimeout(() => {
          item.classList.remove('timeline__item--emphasis');
          dot?.classList.remove('timeline__dot--active');
        }, 1200);
      }

      /* ── Reduced motion fallback ───────────────────────────────────── */

      showAll() {
        for (const item of this.items) {
          item.classList.add('timeline__item--revealed');
        }
      }
    }
  );
}
